import { useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const INIT = {
  full_name: "",
  company_name: "",
  phone: "",
  email: "",
  equipment_type: "",
  num_trucks: "",
  regions: [],
  has_dispatcher: false,
  message: "",
};

export default function QuoteForm() {
  const { t } = useLanguage();
  const [form, setForm] = useState(INIT);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "" }));
  };

  const toggleRegion = (r) => {
    setForm((f) => ({
      ...f,
      regions: f.regions.includes(r) ? f.regions.filter((x) => x !== r) : [...f.regions, r],
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = t.quote.errors.required;
    if (!form.phone.trim()) e.phone = t.quote.errors.required;
    if (!form.email.trim()) e.email = t.quote.errors.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t.quote.errors.invalidEmail;
    if (!form.equipment_type) e.equipment_type = t.quote.errors.required;
    if (!form.num_trucks) e.num_trucks = t.quote.errors.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error(t.quote.errors.fixErrors);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/quote", form);
      setSuccess(true);
      setForm(INIT);
      toast.success(t.quote.errors.success);
    } catch (err) {
      console.error(err);
      toast.error(t.quote.errors.genericFail);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-ink-soft border border-ink-line focus:border-gold rounded px-4 py-3 text-white placeholder:text-white/40 transition-colors focus:outline-none focus:ring-1 focus:ring-gold";

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="relative py-24 md:py-32 bg-gradient-to-b from-ink via-[#0a0a14] to-ink"
    >
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(ellipse at 50% 0%, rgba(255,215,0,0.15), transparent 60%)",
        }}
      />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="text-gold text-sm uppercase tracking-[0.3em] font-semibold mb-4">{t.quote.eyebrow}</div>
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl text-white leading-[0.95] break-words">
            {t.quote.headingStart} <span className="text-gold">{t.quote.headingEnd}</span>
          </h2>
          <p className="mt-6 text-white/70 text-base md:text-lg">{t.quote.sub}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-14 bg-ink-card border border-ink-line rounded-lg p-8 md:p-12"
        >
          {success ? (
            <div data-testid="quote-success" className="flex flex-col items-center text-center py-10">
              <div className="w-20 h-20 rounded-full bg-gold/15 flex items-center justify-center mb-6">
                <CheckCircle2 size={44} className="text-gold" />
              </div>
              <h3 className="font-heading text-4xl text-white mb-4 uppercase">{t.quote.successHeading}</h3>
              <p className="text-white/70 max-w-lg">{t.quote.successDesc}</p>
              <button
                onClick={() => setSuccess(false)}
                className="mt-8 text-gold hover:underline text-sm font-semibold uppercase tracking-wider"
                data-testid="submit-another"
              >
                {t.quote.submitAnother}
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="quote-form" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-white/70 font-semibold mb-2">
                    {t.quote.labels.fullName}
                  </label>
                  <input
                    data-testid="quote-full_name"
                    className={inputCls}
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    placeholder={t.quote.placeholders.fullName}
                  />
                  {errors.full_name && <p className="mt-1 text-red-400 text-xs">{errors.full_name}</p>}
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-white/70 font-semibold mb-2">
                    {t.quote.labels.companyName}
                  </label>
                  <input
                    data-testid="quote-company_name"
                    className={inputCls}
                    value={form.company_name}
                    onChange={(e) => update("company_name", e.target.value)}
                    placeholder={t.quote.placeholders.companyName}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-white/70 font-semibold mb-2">
                    {t.quote.labels.phone}
                  </label>
                  <input
                    data-testid="quote-phone"
                    className={inputCls}
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder={t.quote.placeholders.phone}
                  />
                  {errors.phone && <p className="mt-1 text-red-400 text-xs">{errors.phone}</p>}
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-white/70 font-semibold mb-2">
                    {t.quote.labels.email}
                  </label>
                  <input
                    data-testid="quote-email"
                    type="email"
                    className={inputCls}
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder={t.quote.placeholders.email}
                  />
                  {errors.email && <p className="mt-1 text-red-400 text-xs">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-white/70 font-semibold mb-2">
                    {t.quote.labels.equipment}
                  </label>
                  <select
                    data-testid="quote-equipment_type"
                    className={inputCls}
                    value={form.equipment_type}
                    onChange={(e) => update("equipment_type", e.target.value)}
                  >
                    <option value="">{t.quote.placeholders.select}</option>
                    {t.quote.equipment.map((e) => (
                      <option key={e} value={e}>{e}</option>
                    ))}
                  </select>
                  {errors.equipment_type && <p className="mt-1 text-red-400 text-xs">{errors.equipment_type}</p>}
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-white/70 font-semibold mb-2">
                    {t.quote.labels.trucks}
                  </label>
                  <select
                    data-testid="quote-num_trucks"
                    className={inputCls}
                    value={form.num_trucks}
                    onChange={(e) => update("num_trucks", e.target.value)}
                  >
                    <option value="">{t.quote.placeholders.select}</option>
                    {t.quote.truckCounts.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  {errors.num_trucks && <p className="mt-1 text-red-400 text-xs">{errors.num_trucks}</p>}
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-white/70 font-semibold mb-3">
                  {t.quote.labels.regions}
                </label>
                <div className="flex flex-wrap gap-2">
                  {t.quote.regions.map((r) => {
                    const active = form.regions.includes(r);
                    return (
                      <button
                        type="button"
                        key={r}
                        data-testid={`region-${r.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => toggleRegion(r)}
                        className={`px-4 py-2 rounded border text-sm font-semibold transition-all ${
                          active
                            ? "bg-gold text-ink border-gold"
                            : "bg-ink-soft text-white/80 border-ink-line hover:border-gold/50"
                        }`}
                      >
                        {r}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-white/70 font-semibold mb-3">
                  {t.quote.labels.hasDispatcher}
                </label>
                <div className="flex gap-3">
                  {[
                    { label: t.quote.no, val: false },
                    { label: t.quote.yes, val: true },
                  ].map((o) => (
                    <button
                      key={o.label}
                      type="button"
                      data-testid={`dispatcher-${o.val ? "yes" : "no"}`}
                      onClick={() => update("has_dispatcher", o.val)}
                      className={`px-6 py-2.5 rounded border text-sm font-bold transition-all ${
                        form.has_dispatcher === o.val
                          ? "bg-gold text-ink border-gold"
                          : "bg-ink-soft text-white/80 border-ink-line hover:border-gold/50"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-white/70 font-semibold mb-2">
                  {t.quote.labels.message}
                </label>
                <textarea
                  data-testid="quote-message"
                  rows={4}
                  className={inputCls}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder={t.quote.placeholders.message}
                />
              </div>

              <button
                type="submit"
                data-testid="quote-form-submit"
                disabled={submitting}
                className="group w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest px-8 py-4 rounded hover:bg-gold-hover transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,215,0,0.5)] disabled:opacity-60"
              >
                {submitting ? t.quote.submitting : t.quote.submit}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
